<script>
  import { createEventDispatcher, onMount } from "svelte";
  import TooltipHybridButton from "../search/TooltipHybridButton.svelte";

  export let expanded = false;
  export let dataTypes = [];

  const dispatch = createEventDispatcher();

  let searchQuery = "";
  let localDataTypes = [];

  onMount(() => {
    if (dataTypes && dataTypes.length > 0) {
      localDataTypes = dataTypes;
    }
  });

  // Update localDataTypes only when dataTypes changes (not on every render)
  $: if (dataTypes && dataTypes.length > 0 && !localDataTypes.length) {
    localDataTypes = dataTypes;
  }

  // Check if any dataType has groups property
  $: hasGroups = localDataTypes.some((type) => type.groups?.length > 0);

  // Modified to handle both grouped and ungrouped cases
  $: groupedTypes = getGroupedTypes(localDataTypes, searchQuery.toLowerCase());

  function getGroupedTypes(types, query) {
    const filtered = query
      ? types.filter(
        type => type.name?.toLowerCase().includes(query.toLowerCase()) || type.id?.toLowerCase().includes(query.toLowerCase())
      )
      : types;

    // If no types have groups, return single flat list
    if (!hasGroups) {
      return [
        {
          name: null, // null indicates ungrouped
          types: filtered
        }
      ];
    }

    // Original grouping logic for when groups exist
    const groupMap = filtered.reduce((acc, type) => {
      const groupName = type.groups?.[0] || "Other";
      if (!acc[groupName]) {
        acc[groupName] = { name: groupName, types: [] };
      }
      acc[groupName].types.push(type);
      return acc;
    }, {});

    return Object.values(groupMap);
  }

  function handleGroupToggle(groupName) {
    const group = groupedTypes.find((g) => g.name === groupName);
    if (!group) return;

    const allSelected = group.types.every((type) => type.selected);

    // Toggle selection for all items in this group
    localDataTypes = localDataTypes.map((dt) => {
      const inGroup = group.types.some((t) => t.id === dt.id);
      if (inGroup) {
        return {
          ...dt,
          selected: !allSelected
        };
      }
      return dt;
    });

    // Notify parent of change
    dispatch("change", { dataTypes: localDataTypes });
  }

  function handleTypeToggle(typeId) {
    // Toggle selection for the specific type
    localDataTypes = localDataTypes.map((dt) => {
      if (dt.id === typeId) {
        return {
          ...dt,
          selected: !dt.selected
        };
      }
      return dt;
    });

    // Notify parent of change
    dispatch("change", { dataTypes: localDataTypes });
  }

  function handleSelectAll() {
    // Set all as selected
    localDataTypes = localDataTypes.map((dt) => ({
      ...dt,
      selected: true
    }));

    // Notify parent of change
    dispatch("change", { dataTypes: localDataTypes });
  }

  function handleSelectNone() {
    // Set all as not selected
    localDataTypes = localDataTypes.map((dt) => ({
      ...dt,
      selected: false
    }));

    // Notify parent of change
    dispatch("change", { dataTypes: localDataTypes });
  }

  function isGroupSelected(group) {
    return group.types.every((type) => type.selected);
  }

  function isGroupIndeterminate(group) {
    const selectedCount = group.types.filter((type) => type.selected).length;
    return selectedCount > 0 && selectedCount < group.types.length;
  }

  // Check if all data types are selected
  $: allSelected = localDataTypes.length > 0 && localDataTypes.every((dt) => dt.selected);

  // Check if no data types are selected
  $: noneSelected = localDataTypes.length > 0 && localDataTypes.every((dt) => !dt.selected);
</script>

{#if expanded}
  <div class="selector-content">
    <div class="selection-controls">
      <label class="radio-label">
        <input
          type="radio"
          name="selection"
          value="all"
          on:change={handleSelectAll}
          checked={allSelected}
        />
        <span>Select All</span>
      </label>
      <label class="radio-label">
        <input
          type="radio"
          name="selection"
          value="none"
          on:change={handleSelectNone}
          checked={noneSelected}
        />
        <span>Select None</span>
      </label>
    </div>

    <div class="search-container">
      <input
        type="text"
        class="form-control"
        placeholder="Search data types..."
        bind:value={searchQuery}
      />
    </div>

    <div class="data-types-list">
      {#each groupedTypes as group (group.name)}
        <div class="group-section">
          {#if hasGroups}
            <!-- Show group header only when groups exist -->
            <div class="group-header">
              <label class="group-label">
                <input
                  type="checkbox"
                  class="form-check-input"
                  checked={isGroupSelected(group)}
                  class:indeterminate={isGroupIndeterminate(group)}
                  on:change={() => handleGroupToggle(group.name)}
                />
                <span class="fw-bold">{group.name}</span>
              </label>
            </div>
          {/if}

          <div class="types-container" class:no-padding={!hasGroups}>
            {#each group.types as type (type.id)}
              <div class="type-item">
                <label class="type-label">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    checked={type.selected}
                    on:change={() => handleTypeToggle(type.id)}
                  />
                  <span>{type.name}</span>
                  {#if type.description}
                    <TooltipHybridButton
                      searchUrlQueryParam={type.id}
                      tooltip={type.description}
                    />
                    <!--                    <i class="bi bi-question-circle-fill ms-1" id={`tooltip-${type.id}`} />-->
                    <!--                    <Tooltip target={`tooltip-${type.id}`} placement="right">-->
                    <!--                      {type.description}-->
                    <!--                    </Tooltip>-->
                  {/if}
                </label>
              </div>
            {/each}
          </div>
        </div>
      {/each}
    </div>

    <div class="action-footer">
      {#if noneSelected}
        <div class="warning-message">
          <i class="bi bi-exclamation-triangle-fill text-warning me-1"></i>
          At least one data type must be selected
        </div>
      {/if}
      <div class="button-group">
        <button
          type="button"
          class="btn btn-secondary btn-sm me-2"
          on:click={() => (expanded = false)}
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          disabled={noneSelected}
          on:click={() => {
            dispatch("change", { dataTypes: localDataTypes });
            expanded = false;
          }}
        >
          Accept
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
    .selector-content {
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 1rem;
        background-color: #f8f9fa;
        max-width: 100%;
    }

    .selection-controls {
        display: flex;
        gap: 1.5rem;
        margin-bottom: 1rem;
    }

    .radio-label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;
        margin: 0;
    }

    .radio-label input[type="radio"] {
        margin: 0;
    }

    .search-container {
        background: white;
        margin-bottom: 1rem;
        position: sticky;
        top: 0;
        z-index: 1;
    }

    .data-types-list {
        max-height: 400px;
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: #6c757d transparent;
    }

    .group-section {
        margin-bottom: 1rem;
        background: white;
        padding: 0.75rem;
        border-radius: 4px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .group-header {
        margin-bottom: 0.5rem;
    }

    .group-label,
    .type-label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;
        margin: 0;
    }

    .types-container {
        padding-left: 1.5rem;
    }

    .types-container.no-padding {
        padding-left: 0;
    }

    .type-item {
        margin: 0.5rem 0;
    }

    .action-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid #dee2e6;
        margin-top: 1rem;
        padding-top: 1rem;
    }

    .warning-message {
        font-size: 0.875rem;
        color: #666;
        flex: 1;
    }

    .button-group {
        display: flex;
        gap: 0.5rem;
    }

    :global(.form-check-input.indeterminate) {
        background-color: #6c757d;
        border-color: #6c757d;
    }
</style>
